import React, {useEffect, useMemo, useState} from 'react';
import {Alert, Box, Typography} from '@mui/material';
import {useDispatch, useSelector} from 'react-redux';
import {t} from 'i18next';
import './SourceSelectionStep.scss';
import ArrSingleSelectDropdown from '../../../../../common/arr-select-dropdown/ArrSingleSelectDropdown';
import ArrTreeView from '../../../../../common/arr-tree-view/ArrTreeView';
import {ISelectItem} from '../../../../../../shared/types';
import {RootState} from '../../../../../../redux/store';
import {AI_REVIEW_JOB_TYPE} from '../../../constant/AiReviewConstants';
import {TreeViewBaseItem} from '@mui/x-tree-view/models';
import {ExtendedTreeItemProps} from '../../../../../../shared/interfaces';
import {ListItemsExtended, treeDataSourceSelection} from './utils';
import {
  useGetFoldersList,
  useGetTagsList,
} from '../../../../../../hooks/tagsFoldering/useTagsFoldering';
import ArrLoadingSpinner from '../../../../../common/arr-loading-spinner/ArrLoadingSpinner';
import {setFormData} from '../../../../../../redux/slice/aiReviewSliceV2';
export type SourceSelectionMode = 'SAVEDSEARCH' | 'TAG' | 'FOLDER';
import {useGetSavedSearchList} from '../../../../../../hooks/ai/investigate/useInvestigate';
import {useGetGroundTruthTags} from '../../../../../../hooks/ai/review/useReview';
import {numberFormatter} from '../../../../../../utils/utils';
import {ReactComponent as CheckmarkIcon} from '../../../../../../assets/images/checkmark.svg';

type SourceSelectionStepProps = {
  onSelectionChange?: (ids: string[]) => void;
};

const SourceSelectionStep: React.FC<SourceSelectionStepProps> = ({
  onSelectionChange,
}) => {
  const userSecurityGroupId = useSelector(
    (state: RootState) => state.authenticatedUser.user.securityGroupId
  );
  const {
    sourceSelectionMode: selectionMode,
    sourceSelection,
    sourceSelectionName,
    totalDocs: storedTotalDocs,
    groundTruthTagId,
  } = useSelector((state: RootState) => state.aiReviewV2.formData);
  const jobType = useSelector((state: RootState) => state.aiReviewV2.jobType);
  const [selectedTag, setSelectedTag] = useState<ISelectItem | null>(null);
  const [selectedTreeItem, setSelectedTreeItem] = useState<string[] | null>(
    null
  );
  const [treeData, setTreeData] = useState<ListItemsExtended[]>([]);
  const [isTreeLoading, setIsTreeLoading] = useState(false);
  const dispatch = useDispatch();

  const hasRightColumn = jobType === AI_REVIEW_JOB_TYPE.relevancy;

  const {
    data: groundTruthTagsResponse,
    isLoading: isGroundTruthTagsLoading,
    isFetching: isGroundTruthTagsFetching,
    isError: _isGroundTruthTagsErrors,
  } = useGetGroundTruthTags(hasRightColumn);

  const groundTruthTagOptions: ISelectItem[] = useMemo(() => {
    if (!groundTruthTagsResponse?.tags?.length) {
      return [];
    }

    return groundTruthTagsResponse.tags.map((tag) => {
      const documentCount =
        typeof tag.documentCount === 'number' ? tag.documentCount : null;

      return {
        id: String(tag.tagId),
        name: tag.tagLabel,
        documentCount,
        disabled: documentCount === 0,
      };
    });
  }, [groundTruthTagsResponse]);

  const {
    mutate: fetchSavedSearchList,
    data: savedSearchList,
    isPending: isLoadingSavedSearchList,
  } = useGetSavedSearchList();

  const {
    data: foldersList,
    isLoading: isLoadingFoldersList,
    refetch: refetchGetFolderList,
  } = useGetFoldersList(
    `?securityGroupId=${userSecurityGroupId}&toggelValue=1`,
    false,
    0
  );

  const {
    data: tagsList,
    isLoading: isLoadingTagsList,
    refetch: refetchGetTagList,
  } = useGetTagsList(
    `?securityGroupId=${userSecurityGroupId}&toggelValue=1`,
    false,
    0
  );

  useEffect(() => {
    if (sourceSelection && sourceSelection !== 0) {
      setSelectedTreeItem([String(sourceSelection)]);
    }
  }, [sourceSelection]);

  useEffect(() => {
    if (!groundTruthTagId || groundTruthTagOptions.length === 0) return;

    const savedTag = groundTruthTagOptions.find(
      (opt) =>
        Number(opt.id) === Number(groundTruthTagId) ||
        opt.name === String(groundTruthTagId)
    );
    if (savedTag) {
      setSelectedTag(savedTag ?? null);
    }
  }, [groundTruthTagId, groundTruthTagOptions]);

  useEffect(() => {
    const loadTreeData = (data: unknown) => {
      setIsTreeLoading(true);
      const roots = treeDataSourceSelection(data);
      setTreeData(roots);
      setIsTreeLoading(false);
    };

    switch (selectionMode) {
      case 'SAVEDSEARCH':
        if (!savedSearchList) {
          fetchSavedSearchList(true);
          return;
        }
        loadTreeData(savedSearchList);
        break;
      case 'TAG':
        if (!tagsList?.tagList) {
          refetchGetTagList();
          return;
        }
        loadTreeData(tagsList.tagList);
        break;
      case 'FOLDER':
        if (!foldersList) {
          refetchGetFolderList();
          return;
        }
        loadTreeData(foldersList);
        break;
      default:
        break;
    }
  }, [
    selectionMode,
    savedSearchList,
    tagsList,
    foldersList,
    fetchSavedSearchList,
    refetchGetTagList,
    refetchGetFolderList,
  ]);

  type TreeNode = (typeof treeData)[number];

  const findNodeById = (nodes: TreeNode[], id: string): TreeNode | null => {
    for (const node of nodes) {
      if (String(node.id) === id) return node;
      if (node.children && node.children.length > 0) {
        const found = findNodeById(node.children as TreeNode[], id);
        if (found) return found;
      }
    }
    return null;
  };

  const selectedTreeNode = useMemo(() => {
    if (!selectedTreeItem?.[0] || treeData.length === 0) return null;
    return findNodeById(treeData, selectedTreeItem[0]);
  }, [selectedTreeItem, treeData]);

  const totalDocs = selectedTreeNode?.documentCount ?? '---';

  useEffect(() => {
    onSelectionChange?.(Number(totalDocs) > 0 ? (selectedTreeItem ?? []) : []);
  }, [totalDocs]);

  useEffect(() => {
    if (!selectedTreeItem?.[0]) return;
    if (!selectedTreeNode) return;

    const nextName = selectedTreeNode.label ?? '';
    const nextTotalDocs = selectedTreeNode.documentCount ?? null;

    if (sourceSelectionName === nextName && storedTotalDocs === nextTotalDocs) {
      return;
    }

    dispatch(
      setFormData({
        sourceSelectionName: nextName,
        totalDocs: nextTotalDocs,
      })
    );
  }, [
    selectedTreeItem,
    selectedTreeNode,
    sourceSelectionName,
    storedTotalDocs,
    dispatch,
  ]);

  const selectMode = (mode: SourceSelectionMode) => {
    if (selectionMode !== mode) {
      dispatch(
        setFormData({
          sourceSelectionMode: mode,
          sourceSelection: 0,
          sourceSelectionName: '',
          totalDocs: null,
        })
      );
      setSelectedTreeItem(null);
      onSelectionChange?.([]);
    }
  };

  const sourceModeOptions: ISelectItem[] = useMemo(
    () => [
      {
        id: 'SAVEDSEARCH',
        name: t('ai.review.sourceSelectionStep.selectSearches'),
      },
      {
        id: 'TAG',
        name: t('ai.review.sourceSelectionStep.selectTags'),
      },
      {
        id: 'FOLDER',
        name: t('ai.review.sourceSelectionStep.selectFolders'),
      },
    ],
    []
  );

  const selectedSourceMode = useMemo(
    () =>
      sourceModeOptions.find((option) => option.id === selectionMode) ??
      sourceModeOptions[0],
    [selectionMode, sourceModeOptions]
  );

  const handleSourceModeChange = (mode: ISelectItem | null) => {
    if (!mode?.id) return;
    selectMode(mode.id as SourceSelectionMode);
  };

  return (
    <Box
      className="source-selection"
      component="section"
      role="region"
      aria-labelledby="source-selection-heading"
      data-testid="source-selection-container"
    >
      <Box
        className={`source-selection__column ${
          !hasRightColumn ? 'source-selection__column--single' : ''
        }`}
        data-testid="source-selection-column-grouping"
      >
        <Box
          className="source-selection__source-type"
          data-testid="source-selection-mode-dropdown"
        >
          <Typography
            component="h2"
            className="typo-text subhead5_size source-selection__source-type-label text-body-strong"
            id="source-selection-heading"
            data-testid="source-selection-source-type-label"
          >
            {t('ai.review.sourceSelectionStep.sourceType')}
          </Typography>
          <ArrSingleSelectDropdown
            className="source-selection__mode-select"
            customWidth={20}
            dropdownLabel={t(
              'ai.review.sourceSelectionStep.sourceSelectionMode'
            )}
            dropdownItems={sourceModeOptions}
            dropdownItemDisplay="name"
            dropdownItemKey="id"
            selectedValues={selectedSourceMode}
            onSelectionChange={handleSourceModeChange}
            searchable={false}
            sizeVariant="medium"
            dataTestId="asd-source-selection-mode-select"
            inputLabelClassName="display-none"
            inputLabelProps={{shrink: false}}
            selectRestProps={{
              'aria-labelledby': 'source-selection-heading',
            }}
          />
        </Box>

        <Box
          className="source-selection__tree-view"
          aria-label={t('ai.review.sourceSelectionStep.documentsTreeView')}
          data-testid="source-selection-tree-view"
        >
          {isTreeLoading ||
          isLoadingSavedSearchList ||
          isLoadingFoldersList ||
          isLoadingTagsList ? (
            <Box className="tree-view-loader">
              <ArrLoadingSpinner />
            </Box>
          ) : (
            <ArrTreeView
              multipleSelection={false}
              selectedItem={
                selectedTreeItem && selectedTreeItem.length > 0
                  ? selectedTreeItem[0]
                  : null
              }
              setSelectedItem={(ids) => {
                setSelectedTreeItem(ids);
                const selectedId = ids?.[0];
                const node = selectedId
                  ? findNodeById(treeData, selectedId)
                  : null;
                dispatch(
                  setFormData({
                    sourceSelection: Number(selectedId ?? 0),
                    sourceSelectionName: node?.label ?? '',
                    totalDocs: node?.documentCount ?? null,
                  })
                );
              }}
              itemsList={treeData as TreeViewBaseItem<ExtendedTreeItemProps>[]}
              isShowCheckbox={true}
              isShowCountLabel={true}
              handleNoResultMessage={t('common.noResultFound')}
              expandOnSearch={false}
              showTooltip
              isBlockParentNodeSelection={true}
            />
          )}
        </Box>
      </Box>

      {hasRightColumn && (
        <Box
          className="source-selection__column--right"
          data-testid="source-selection-step-ground-truth-field-wrapper"
          role="region"
          aria-labelledby="source-selection-ground-truth-heading"
          aria-describedby="source-selection-ground-truth-description"
        >
          <Typography
            component="h2"
            className="typo-text subhead4_size source-selection__ground-truth-title"
            id="source-selection-ground-truth-heading"
            data-testid="source-selection-step-ground-truth-field"
          >
            {t('ai.review.sourceSelectionStep.groundTruthField')}{' '}
            <span
              className="source-selection__ground-truth-optional text-body-subtle"
              data-testid="source-selection-step-optional"
            >
              {t('ai.review.sourceSelectionStep.optional')}
            </span>
          </Typography>
          <Typography
            className="typo-text body3_size source-selection__ground-truth-description-text text-body-subtle"
            data-testid="source-selection-step-ground-truth-description"
            id="source-selection-ground-truth-description"
          >
            {t('ai.review.sourceSelectionStep.groundTruthDescription')}
          </Typography>
          <Box
            className="source-selection__ground-truth-select"
            data-testid="source-selection-drop-down"
          >
            <ArrSingleSelectDropdown
              className="source-selection__ground-truth-dropdown"
              customWidth={20}
              dropdownLabel={t('ai.review.sourceSelectionStep.selectATag')}
              dropdownItems={groundTruthTagOptions}
              selectedValues={selectedTag}
              dropdownItemDisplay="name"
              dropdownItemKey="id"
              disabled={
                isGroundTruthTagsLoading ||
                isGroundTruthTagsFetching ||
                groundTruthTagOptions.length === 0
              }
              onSelectionChange={(value) => {
                setSelectedTag(value);
                dispatch(
                  setFormData({
                    groundTruthTagId: value?.id ? Number(value.id) : null,
                  })
                );
              }}
              dataTestId="asd-ground-truth-tag-select"
              sizeVariant="medium"
              menuPropClassName="source-selection-ground-truth-menu"
              selectRestProps={{
                'aria-labelledby': 'source-selection-ground-truth-heading',
                'aria-describedby': 'source-selection-ground-truth-description',
              }}
              renderSuffix={(item) => {
                const documentCount = item.documentCount;
                const isSelected =
                  selectedTag?.id != null &&
                  item?.id != null &&
                  selectedTag.id === item.id;
                const isZeroCount = documentCount === 0;

                return (
                  <>
                    {typeof documentCount === 'number' && (
                      <span
                        className={
                          isZeroCount
                            ? 'source-selection-option-count--inactive'
                            : 'source-selection-option-count'
                        }
                      >
                        {`(${numberFormatter.format(documentCount)})`}
                      </span>
                    )}
                    {isSelected && (
                      <CheckmarkIcon
                        className="source-selection-option-check"
                        aria-hidden="true"
                      />
                    )}
                  </>
                );
              }}
              inputLabelClassName={selectedTag ? 'display-none' : undefined}
              inputLabelProps={{shrink: false}}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default SourceSelectionStep;
